import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  campaignsQuery,
  INACTIVE_AFTER_DAYS,
  inFlight,
  progressOf,
  useCreateCampaign,
  type Campaign,
} from "../components/campaigns/api";
import { Button } from "../components/ui/Button";
import { useCan } from "../hooks/useCan";
import { formatDateTime } from "../lib/format";
import styles from "../components/campaigns/Campaign.module.css";

export const Route = createFileRoute("/_auth/_messaging/campaigns/")({
  staticData: { titleKey: "nav.messaging", breadcrumbKey: "breadcrumb.operate", breadcrumbN: 7 },
  component: CampaignsIndex,
});

function CampaignsIndex() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = useCan("manage_surveys");
  const { data, isLoading } = useQuery(campaignsQuery);
  const create = useCreateCampaign();
  const [name, setName] = useState("");
  // The server lists inactive campaigns after the live ones.
  const items = data?.items ?? [];
  const firstInactive = items.findIndex((c) => c.inactive_reason);
  const inactiveCount = firstInactive < 0 ? 0 : items.length - firstInactive;

  function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim() },
      {
        onSuccess: (c) => {
          setName("");
          void navigate({ to: "/campaigns/$id", params: { id: c.id } });
        },
      },
    );
  }

  return (
    <div className={styles.wrap}>
      {canManage && (
        <form onSubmit={onCreate} className={styles.newRow}>
          <input
            className={styles.textInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("campaign.newPlaceholder")}
            aria-label={t("campaign.newPlaceholder")}
          />
          <Button
            type="submit"
            tone="primary"
            variant="solid"
            disabled={!name.trim() || create.isPending}
          >
            {t("campaign.create")}
          </Button>
        </form>
      )}

      {isLoading && <span className={styles.note}>…</span>}
      {data?.items.length === 0 && <span className={styles.note}>{t("campaign.empty")}</span>}

      {(data?.items.length ?? 0) > 0 && (
        <div className={styles.table}>
          <div className={`${styles.tRow} ${styles.tHead}`}>
            <span>{t("campaign.list.campaign")}</span>
            <span className={styles.tBody}>{t("campaign.message")}</span>
            <span>{t("campaign.list.status")}</span>
            <span>{t("campaign.list.reached")}</span>
            <span>{t("campaign.list.date")}</span>
          </div>
          {items.map((c, i) => (
            <Fragment key={c.id}>
              {i === firstInactive && (
                <div className={styles.tDivider}>
                  {t("campaign.list.inactiveHead", { n: inactiveCount })}
                </div>
              )}
              <Link
                to="/campaigns/$id"
                params={{ id: c.id }}
                className={`${styles.tRow} ${c.inactive_reason ? styles.tRowInactive : ""}`}
              >
                <span className={styles.listName}>
                  {c.name}
                  {c.promo_name && <small className={styles.promoTag}>◆ {c.promo_name}</small>}
                </span>
                <span className={`${styles.listBody} ${styles.tBody}`}>
                  {c.body || t("campaign.noBody")}
                </span>
                <CampaignState c={c} />
                <span className={styles.reached}>
                  {sentCount(c) > 0 ? (
                    <>
                      {progressOf(c).sent}
                      {progressOf(c).failed > 0 && (
                        <small> · {t("campaign.list.failedN", { n: progressOf(c).failed })}</small>
                      )}
                    </>
                  ) : (
                    "—"
                  )}
                </span>
                <span className={styles.listDate}>{formatDateTime(c.updated_at)}</span>
              </Link>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

const sentCount = (c: Campaign) => {
  const p = progressOf(c);
  return p.sent + p.failed + p.pending + p.sending;
};

/** Draft, still going out (or waiting on the daily cap), sent, or inactive —
 *  with a bar once anything went out. */
function CampaignState({ c }: { c: Campaign }) {
  const { t } = useTranslation();
  const p = progressOf(c);
  const total = sentCount(c);
  const state = c.inactive_reason
    ? "inactive"
    : total === 0
      ? "draft"
      : inFlight(p)
        ? "pending"
        : "sent";
  return (
    <span className={styles.stateCell}>
      <span className={`${styles.statePill} ${styles[`state_${state}`]}`}>
        {state === "pending"
          ? t("campaign.list.state.pending", { n: p.pending + p.sending })
          : t(`campaign.list.state.${state}`)}
      </span>
      {c.inactive_reason && (
        <small className={styles.inactiveWhy}>
          {t(`campaign.inactive.${c.inactive_reason}`, { days: INACTIVE_AFTER_DAYS })}
        </small>
      )}
      {total > 0 && (
        <span className={styles.progressBar} aria-hidden>
          <i className={styles.p_sent} style={{ flexGrow: p.sent }} />
          <i className={styles.p_failed} style={{ flexGrow: p.failed }} />
          <i className={styles.p_pending} style={{ flexGrow: p.pending + p.sending }} />
        </span>
      )}
    </span>
  );
}
